import UrlParamsContext from 'core/contexts/UrlParamsContextDef'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { useCallback, useContext, useMemo, useRef } from 'react'

export type useUrlParamsReturn = [
  URLSearchParams,
  (d: URLSearchParams, pathname?: string) => void,
  (pathname: string) => void
]

/**
 * @description: A convenient hook that allows reading and writing into the current URL query params, typically used for storing view states like page limit, filtering, sorting info, etc; This also wires with the global client state to store the path and url specific association. Read more on useSettings' type definition.
 * @example: To read the current page url query, use the first element of the returned tuple. To write into the current page url query, call the 2nd element of the tuple with the changed. Note that this hook works with Javascript URLSearchParams and not a plain object.
 * @returns: A tuple [urlParams, setURLParams]. The first is an URLSearchParams object, the second is a function to modify the page, with the argument being the new URLSearchParams. Calling the setter will push to the history stack.
 */
const useUrlParams = (remember = true) => {
  const { search, replace, pathname } = useNavigation()
  const { urlQueries, setUrlQueries } = useContext(UrlParamsContext)

  const urlQueriesRef = useRef(urlQueries)
  urlQueriesRef.current = urlQueries
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  const setUrlParams = useCallback(
    (params: URLSearchParams, newPath = pathnameRef.current) => {
      const queryString = params.toString()
      const currentUrlQueries = urlQueriesRef.current
      const urlQueriesCopy = { ...currentUrlQueries }
      if (queryString) {
        setUrlQueries(
          {
            ...currentUrlQueries,
            [newPath]: queryString
          },
          remember
        )
        replace({
          pathname: newPath,
          search: queryString
        })
        return
      }
      delete urlQueriesCopy[newPath]
      setUrlQueries(urlQueriesCopy)
      replace({
        pathname: newPath
      })
    },
    [remember, replace, setUrlQueries]
  )

  const rawSearch = search || urlQueries[pathname] || ''

  const urlParams = useMemo(() => {
    return new URLSearchParams(rawSearch)
  }, [rawSearch])

  const forgetUrlParams = useCallback(
    (pathname: string) => {
      const currentUrlQueries = urlQueriesRef.current
      const urlQueriesCopy = { ...currentUrlQueries }
      delete urlQueriesCopy[pathname]
      setUrlQueries(urlQueriesCopy)
    },
    [setUrlQueries]
  )

  return [urlParams, setUrlParams, forgetUrlParams] as useUrlParamsReturn
}

export default useUrlParams
